import axios, { AxiosRequestConfig, CancelTokenSource } from "axios"; // tslint:disable-line match-default-export-name
import md5 from "md5";

import { asyncTimeout } from "@use/async-timeout";
import { hrtime } from "@use/universal-hrtime";

import { CollectionModel } from "./Collection";
import { ObservableCollection } from "./ObservableCollection";

/**
 * We're using this symbol to safely store an md5 hash of the last saved
 * snapshot, so we know if there are any unsaved changes.
 */
const lastSavedSnapshot = Symbol("SaveableCollection.lastSavedSnapshot");

/**
 * We're using this symbol to safely store a reference to the cancel token,
 * which was associated with the previous attempt to save data.
 */
const cancelSaveToken = Symbol("SaveableCollection.cancelSaveToken");

/**
 * The saveable collection ships with a few built-ins to make it easy for
 * developers to implement gracious handling of network based saving.
 *
 * Please note that it's entirely up to the implementation of the collection, if
 * it saves changes made within the collection items or only saves the
 * configurations of the collection itself.
 */
export abstract class SaveableCollection<
  T extends CollectionModel
> extends ObservableCollection<T> {
  /**
   * In order to be able to save changes made within this collection, we'll
   * need to know it's id.
   */
  public abstract readonly id: string | number;

  /**
   * This will contain an md5 hash of the data snapshot taken last time data
   * was saved for this model. We're using this value in [[hasUnsavedChanges]]
   * to verify if any changes have been made since then.
   */
  private [lastSavedSnapshot]: string;

  /**
   * This will contain a reference to the cancel token associated with the
   * previous attempt to save data - so we can cancel it, in case a new
   * request is going to be submitted.
   */
  private [cancelSaveToken]?: CancelTokenSource;

  /**
   * Make sure that a data snapshot is saved right after the model has been
   * instantiated, so that we can keep track of whether or not there are any
   * unsaved changes.
   */
  constructor() {
    super();

    // Save a data snapshot once the model has been instantiated
    setTimeout(() => {
      this[lastSavedSnapshot] = md5(this.getSnapshot());
    });
  }

  /**
   * Helper that'll determine if there have been made any changes since the
   * last time data was saved.
   */
  public hasUnsavedChanges(): boolean {
    // If data has been changed within this collection itself, then return
    // true
    if (this[lastSavedSnapshot] !== md5(this.getSnapshot())) {
      return true;
    }

    // ... Otherwise check for nested changes
    return super.hasUnsavedChanges();
  }

  /**
   * Saves the data by submitting it to a remote backend
   */
  public async save(minDelay: number = 0): Promise<void> {
    // Extract timestamp for when the request was started
    const startedAt = hrtime();

    // Create a cancel token for the request
    let cancelToken = this[cancelSaveToken];

    // If a request is already running, then cancel it now!
    if (cancelToken) {
      cancelToken.cancel("Only one save requested allowed at a time.");
    }

    // Create a new cancel token for the new request
    this[cancelSaveToken] = cancelToken = axios.CancelToken.source();

    try {
      // Grab a snapshop of the data that we're going to save...
      const snapshot = md5(this.getSnapshot());

      // Perform the request using axious with the configuration created
      // by the createSaveRequest abstract method
      await axios({
        ...this.prepareSaveRequest(),

        // Propagate our internal cancel token, so that
        cancelToken: cancelToken.token,
      });

      // If we get here, data was saved successfully - update the last
      // saved snapshot!
      this[lastSavedSnapshot] = snapshot;

      // Wait the defined minimum delay
      await asyncTimeout(Math.max(0, minDelay - (hrtime() - startedAt)));
    } catch (err) {
      // Wait the defined minimum delay
      await asyncTimeout(Math.max(0, minDelay - (hrtime() - startedAt)));

      if (axios.isCancel(err)) {
        // Log cancelled requests on non-production environments
        if (process.env.NODE_ENV !== "production") {
          // tslint:disable-next-line no-console no-unsafe-any
          console.log(`SaveableCollection.save(): Request was cancelled:`, err);
        }

        // ... But don't actually propagate cancellations as errors that
        // external applications will need to catch!
      } else {
        // ... Otherwise propagate the error
        throw err;
      }
    } finally {
      // Reset reference to the cancel token once the request has been
      // fulfilled
      this[cancelSaveToken] = undefined;
    }
  }

  /**
   * A snapshot should uniquely represent all observable data of the model, so
   * that we can easily determine if changes have been made between two
   * snapshots.
   */
  public abstract getSnapshot(): string;

  /**
   * Implement a method that'll return an axios request configuration, which
   * will be submitted to save the data within the public save method.
   *
   * This way, models only need to worry about creating the proper axios
   * configuration - all handling of redundant request cancellation, snapshot
   * updates etc. are managed directly by the built-in save method.
   */
  protected abstract prepareSaveRequest(): AxiosRequestConfig;
}
